/* eslint-disable @typescript-eslint/no-explicit-any */
import type { SchemaTree, GeneratedSchema, RevertSchema, SchemaExport } from './../types';
import { SchemaNode } from '../node';
import { Permit } from './permit';
import { loopSchema, makeScope } from '../utils';

export class SchemaBranch<T extends GeneratedSchema> {
	readonly $schema: T;

	private _meta: {
		scope: string;
		spacer: string;
	};

	constructor(schema: T, scope = '', spacer = '.') {
		this.$schema = schema;
		this._meta = {
			spacer: spacer,
			scope: scope,
		};
	}

	toJSON(): unknown {
		const branch: [string, any][] = [];
		for (const key in this) {
			if (Object.prototype.hasOwnProperty.call(this, key)) {
				const node = this[key];
				if (typeof key === 'string' && !key.startsWith('$') && !key.startsWith('_')) {
					branch.push([key, node]);
				}
			}
		}

		return Object.fromEntries(branch);
	}

	get $meta() {
		return this._meta;
	}

	get $scope() {
		return this._meta.scope;
	}

	$each(
		nodeHook: (node: SchemaNode, key: string) => void,
		branchHook: (branch: SchemaBranch<any>, key: string) => void = () => undefined
	) {
		loopSchema(this.$schema, nodeHook, (node: SchemaBranch<any>, key: string) => {
			branchHook(node, key);
			node.$each(nodeHook);
		});
	}

	$refactor(prefix = '', spacer = '.') {
		const branchSpacer = this._meta.spacer;
		const isSpacerSame = this._meta.spacer === spacer;

		if (!isSpacerSame) {
			this._meta.scope = this._meta.scope.replaceAll(branchSpacer, spacer);
		}

		this._meta = {
			spacer,
			scope: makeScope(spacer, [prefix, this._meta.scope]),
		};

		loopSchema(
			this.$schema,
			(node) => {
				if (!isSpacerSame) node.$scope = node.$scope.replaceAll(branchSpacer, spacer);
				node.$scope = makeScope(spacer, [prefix, node.$scope]);
			},
			(node) => {
				node.$refactor(prefix, spacer);
			}
		);
	}

	$export() {
		type Tree = SchemaTree<{ $scope: string; $description: string }>;
		const tree: Tree = {};

		loopSchema(
			this.$schema,
			(node, key) => {
				tree[key] = { $scope: node.$scope, $description: node.$description };
			},
			(node, key) => {
				const branch = node.$export();
				tree[key] = branch.schema;
			}
		);

		return {
			schema: tree as SchemaExport<T>,
			meta: this._meta,
		};
	}

	$model(): RevertSchema<T> {
		const schema = JSON.stringify(this.$schema);
		return JSON.parse(schema) as RevertSchema<T>;
	}

	$permit(scopes: (string | SchemaNode)[] = []) {
		const _scopes = scopes.map((s) => Permit.getNodeScope(s));
		return new Permit(this.$schema, _scopes);
	}
}